import { useLazyQuery, useMutation } from '@apollo/client';
import { StoreLogin } from '@base/utility/LoginUtility';
import {
  Actions,
  Backdrop,
  Banner,
  Description,
} from '@core/components/Signup/RegisterBanner/RegisterBanner.styled';
import {
  CREATE_TOKEN_ACCOUNT,
  GET_SIM_CONTENT,
  GET_VH_CONTENT,
  REFRESH_TOKEN,
} from '@core/components/Signup/RegisterForm/RegisterForm.query';
import { StyledError } from '@core/components/Users/BulkInvite/BulkInviteModal.styled';
import tracker from '@core/helpers/tracker';
import useUser from '@core/helpers/useUser';
import { Button, Link } from '@virtidev/toolbox';
import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

export const RegisterBanner = ({ type, sharetoken, loggedin }) => {
  const { Email } = useUser();
  const history = useHistory();
  const [error, setError] = useState(/** @type {string |null} */ (null));
  const [createAccount, { loading }] = useMutation(CREATE_TOKEN_ACCOUNT, {
    onError: (error) => {
      setError(error.message);
    },
    notifyOnNetworkStatusChange: true,
  });

  const [getVHContent] = useLazyQuery(GET_VH_CONTENT, {
    variables: {
      sharetokenHeader: sharetoken,
      filter: { ShareToken: { eq: sharetoken } },
    },
  });

  const [getSimContent] = useLazyQuery(GET_SIM_CONTENT, {
    variables: {
      sharetokenHeader: sharetoken,
      filter: { ShareToken: { eq: sharetoken } },
    },
  });

  const [refreshToken] = useMutation(REFRESH_TOKEN);

  const description = useMemo(() => {
    if (loggedin) {
      return "Looks like you're logged in but not part of this organisation.";
    }
    if (type === 'simulation') {
      return 'Create a Virti account to see your performance.';
    }
    if (type === 'virtualhuman') {
      return 'Create a Virti account to see your performance.';
    }
    return null;
  }, [type, loggedin]);

  const search = useMemo(() => {
    const params = new URLSearchParams({
      type,
      sharetoken,
    });

    return params.toString();
  }, [type, sharetoken]);

  const signupUser = useCallback(async () => {
    const { data: accountData } = await createAccount({
      variables: { Email, tokenType: type, token: sharetoken },
    });

    let orgId = null;
    let content = null;

    if (type === 'virtualhuman') {
      const { data } = await getVHContent();

      content = data.readOneVirtualHuman;
    }
    if (type === 'simulation') {
      const { data } = await getSimContent();

      content = data.readOneSimulation;
    }
    if (content?.ShareToken !== sharetoken) {
      content = null;
    }
    orgId = content?.OrganisationID;

    if (orgId) {
      const refreshData = await refreshToken();

      const virtiToken = refreshData?.data?.refreshVirtiToken;
      if (!virtiToken) {
        setError('Unable to reload token, please log out and back in.');
        return;
      }

      StoreLogin(
        virtiToken.Token,
        virtiToken.PDToken,
        accountData.signupTokenUser,
        null,
        orgId
      );

      tracker.track('organisation_registered', {
        organisation_id: orgId,
      });
    }

    if (!content || !orgId) {
      setError('Unable to join the organisation currently.');
      return;
    }
    if (type === 'virtualhuman') {
      history.push(`/my-virtual-humans/${content.ID}/play/${sharetoken}`);
    }
    if (type === 'simulation') {
      history.push(`/my-simulations/${content.ID}/${sharetoken}`);
    }
  }, [
    createAccount,
    Email,
    type,
    sharetoken,
    history,
    getSimContent,
    getVHContent,
    refreshToken,
  ]);

  const [dismissed, setDismissed] = useState(false);

  const dismissViewInAppPrompt = useCallback(() => {
    setDismissed(true);
  }, []);

  if (dismissed) {
    return null;
  }

  return (
    <Backdrop>
      <Banner>
        <Description>
          {error ? (
            <StyledError>{error}</StyledError>
          ) : (
            <div>{description}</div>
          )}
          {!loggedin && (
            <div>
              Already have an account? <Link to="/login">Sign in</Link>
            </div>
          )}
        </Description>
        <Actions>
          <Button color="secondary" onClick={dismissViewInAppPrompt}>
            Dismiss
          </Button>
          {loggedin ? (
            <Button color="turquoise" loading={loading} onClick={signupUser}>
              Join up
            </Button>
          ) : (
            <Button
              type="link"
              color="turquoise"
              to={{ pathname: '/register', search }}
            >
              Create account
            </Button>
          )}
        </Actions>
      </Banner>
    </Backdrop>
  );
};

export default RegisterBanner;
